import * as React from 'react';
import { useTranslation } from 'next-i18next';
import tw, { styled } from 'twin.macro';
import { PlusIcon } from '@heroicons/react/24/outline';

import { EmployerInvoiceCard, SkeletonEmployerCardListLoading, Title, ButtonV2 } from '@restworld/ui-ds';
import { formatDateToDayMonthYear } from '@restworld/utils-common';
import { Invoice } from '@restworld/utility-types';

import { useDownloadInvoiceMutation, useFetchInvoicesQuery } from './employer-invoices.hooks';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IEmployerInvoicesProps {}
export const EmployerInvoices: React.FunctionComponent<IEmployerInvoicesProps> = () => {
  const { invoicesList, query, hasNextPage } = useFetchInvoicesQuery();
  const { mutate: downloadInvoice, isPending: downloadInvoicePending, variables } = useDownloadInvoiceMutation();
  const { t } = useTranslation('payment-and-invoices');

  const getInvoiceStatusInfo = React.useCallback(
    (invoice: Invoice) => {
      if (invoice['paid?']) {
        return {
          status: 'paid' as const,
          statusText: invoice.payment_date
            ? t('paid_on', {
                paymentDate: formatDateToDayMonthYear(invoice.payment_date),
              })
            : t('paid'),
        };
      }
      if (invoice.number_of_installments_paid_for_slots) {
        return {
          status: 'installment' as const,
          statusText: `${invoice.number_of_installments_paid_for_slots}/3`,
        };
      }
      return {
        status: 'to_be_paid' as const,
        statusText: t('to_be_paid'),
      };
    },
    [t]
  );

  const groupedInvoicesList = React.useMemo(
    () =>
      Object.entries(
        invoicesList.reduce((acc, invoice) => {
          const year = new Date(invoice.issue_date).getFullYear();
          if (!acc[year]) acc[year] = [];
          acc[year].push(invoice);
          return acc;
        }, {} as Record<number, Invoice[]>)
      ).sort(([yearA], [yearB]) => Number(yearB) - Number(yearA)),
    [invoicesList]
  );

  const renderInvoicesList = React.useMemo(() => {
    return groupedInvoicesList.map(([year, invoices]) => (
      <li key={year} tw="flex flex-col gap-4">
        <Title fontSize="base" fontWeight="semibold" content={year} textColor="neutral-700" />
        <ul tw="flex flex-col gap-4">
          {invoices.map((invoice) => {
            const {
              id,
              company,
              billing_company_name,
              issue_date,
              quantity,
              discount,
              amount,
              payment_type,
              invoice_number,
              hosted_invoice_url,
            } = invoice;

            const handlePayInvoice = () => {
              if (hosted_invoice_url) {
                window.open(hosted_invoice_url, '_blank');
              }
            };

            const { status, statusText } = getInvoiceStatusInfo(invoice);

            return (
              <li key={id}>
                <EmployerInvoiceCard
                  invoiceNumber={invoice_number}
                  companyName={billing_company_name || company?.name || ''}
                  issueDate={formatDateToDayMonthYear(issue_date)}
                  slots={quantity}
                  discount={discount}
                  amount={amount}
                  status={status}
                  statusText={statusText}
                  onDownloadInvoice={() => downloadInvoice({ id, invoiceNumber: invoice_number })}
                  isAnnualSubscription={payment_type === 'annual_subscription'}
                  downloadInvoicePending={variables?.id === id ? downloadInvoicePending : null}
                  hostedInvoiceUrl={hosted_invoice_url}
                  onPayInvoice={handlePayInvoice}
                />
              </li>
            );
          })}
        </ul>
      </li>
    ));
  }, [groupedInvoicesList, getInvoiceStatusInfo, variables?.id, downloadInvoicePending, downloadInvoice]);

  return (
    <div tw="flex-col justify-start gap-4 inline-flex w-full">
      <EmployerInvoicesHeader />
      {query?.isLoading ? (
        <SkeletonEmployerCardListLoading />
      ) : (
        <ul tw="flex flex-col gap-[64px]">
          {renderInvoicesList}
          {hasNextPage && (
            <div tw="flex justify-center pt-16">
              <ButtonV2
                StartIcon={PlusIcon}
                variant="tertiary"
                onClick={() => query.fetchNextPage()}
                loading={query.isFetchingNextPage}
              >
                {t('load_more_items')}
              </ButtonV2>
            </div>
          )}
        </ul>
      )}
    </div>
  );
};

export default EmployerInvoices;
const EmployerInvoicesHeader = () => {
  const { t } = useTranslation('payment-and-invoices');

  return (
    <HeaderWrapper>
      <TitleWrapper>
        <Title fontSize="large" fontWeight="semibold" content={t('invoices_title')} textColor="neutral-700" />
      </TitleWrapper>
    </HeaderWrapper>
  );
};
const HeaderWrapper = styled.div`
  ${tw`self-stretch h-10 justify-between items-center inline-flex`}
`;
const TitleWrapper = styled.div`
  ${tw`justify-start items-center gap-2 flex`}
`;
